import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Data mode — global Real vs Testing toggle for aggregate/report/export surfaces.
//
//   real    (default): aggregates/reports/exports show ONLY real data.
//   testing (opt-in) : the same surfaces show ONLY testing-project data.
//
// Mutually exclusive (real XOR testing) — never mixed. Persisted in localStorage so it
// survives navigation and reload. Frontend-only, per-browser (intent, not access): the
// admin-only header toggle is the ONLY writer. Non-admins never see it, so they stay 'real'.
//
// Every mode-aware fetch reads `mode` here and passes it as ?data_mode=. Operational
// surfaces (queues, pickers, own-data lists) do NOT read it — they always show everything.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'v2a_data_mode';
const read = () => {
  try { return localStorage.getItem(STORAGE_KEY) === 'testing' ? 'testing' : 'real'; }
  catch { return 'real'; }
};

const DataModeContext = createContext({ mode: 'real', setMode: () => {}, isTesting: false });

export function DataModeProvider({ children }) {
  const [mode, setModeState] = useState(read);

  const setMode = useCallback((m) => {
    const next = m === 'testing' ? 'testing' : 'real';
    setModeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  // Keep multiple open tabs consistent.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setModeState(e.newValue === 'testing' ? 'testing' : 'real');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <DataModeContext.Provider value={{ mode, setMode, isTesting: mode === 'testing' }}>
      {children}
    </DataModeContext.Provider>
  );
}

export function useDataMode() {
  return useContext(DataModeContext);
}
